/**
 * FuelSystem.js - Core fuel management system for Simulation Mode
 *
 * Handles:
 * - Spot price simulation (tied to macro system)
 * - Supplier database and contracts
 * - Hedging mechanics
 * - Negotiation state machine
 */

import { debug } from '../lib/debug.js';

// ============================================================================
// CONSTANTS
// ============================================================================

const BASE_SPOT_PRICE = 2.95; // $/gallon baseline
const PRICE_FLOOR = 2.50;
const PRICE_CEILING = 5.00;

// ============================================================================
// SUPPLIER DATABASE
// ============================================================================

export const FUEL_SUPPLIERS = {
  CASCADE: {
    id: 'CASCADE',
    name: 'Cascade Fuel Services',
    tier: 'REGIONAL',
    baseRate: 2.85,
    description: 'West Coast regional supplier with competitive rates and reliable service.',
    coverage: ['KPDX', 'KSEA', 'KBOI', 'KSFO', 'KOAK', 'KSJC', 'KSMF', 'KGEG', 'KSLC', 'KLAS'],
    strengths: [
      'Lowest rates for Pacific Northwest',
      'Reliable delivery (98% on-time)',
      'Flexible allocation adjustments',
      'No minimum commitment',
    ],
    weaknesses: [
      'West Coast coverage only',
      'No hedging programs available',
      'Limited capacity during supply crunches',
    ],
    reliability: 0.98,
    maintenanceBonus: 0,
    negotiationStyle: 'friendly',
    minContractMonths: 3,
    maxContractMonths: 12,
    maxAllocationGal: 500000,
    minAllocationGal: 50000,
    hedgingAvailable: false,
    unlockedAtTier: 1,
  },

  ATLAS: {
    id: 'ATLAS',
    name: 'Atlas Global Fuel',
    tier: 'MAJOR',
    baseRate: 3.05,
    description: 'National carrier with hedging expertise and price protection programs.',
    coverage: null, // National - all airports
    strengths: [
      'Hedging contracts available',
      'National coverage at all airports',
      'Price lock guarantees',
      'Professional contract terms',
    ],
    weaknesses: [
      'Higher base rate than regional',
      'Strict contract enforcement',
      '6-month minimum commitment',
      'Volume requirements',
    ],
    reliability: 0.95,
    maintenanceBonus: 0,
    negotiationStyle: 'professional',
    minContractMonths: 6,
    maxContractMonths: 24,
    maxAllocationGal: 2000000,
    minAllocationGal: 100000,
    hedgingAvailable: true,
    unlockedAtTier: 2,
  },

  BUDGET: {
    id: 'BUDGET',
    name: 'Budget Fuels LLC',
    tier: 'DISCOUNT',
    baseRate: 2.65,
    description: 'Low-cost option with limited coverage and reliability concerns.',
    coverage: ['KPDX', 'KBOI'],
    strengths: [
      'Lowest rates in market',
      'No minimum commitment',
      'Month-to-month flexibility',
      'Prepay discount available',
    ],
    weaknesses: [
      'Only PDX and BOI coverage',
      '15% delivery failure rate',
      'No support during shortages',
      'Prepayment required',
    ],
    reliability: 0.85,
    maintenanceBonus: 0, // Actually slightly negative effect hidden
    negotiationStyle: 'desperate',
    minContractMonths: 1,
    maxContractMonths: 6,
    maxAllocationGal: 100000,
    minAllocationGal: 10000,
    hedgingAvailable: false,
    unlockedAtTier: 1,
  },

  PREMIUM: {
    id: 'PREMIUM',
    name: 'Premium Petroleum',
    tier: 'PREMIUM',
    baseRate: 3.15,
    description: 'Top-tier fuel with maintenance benefits and priority service.',
    coverage: null, // National + International
    strengths: [
      '-3% maintenance costs',
      'Priority during shortages',
      '99.5% delivery rate',
      'Emergency delivery anywhere',
      'Cleanest fuel in market',
    ],
    weaknesses: [
      'Highest base rate',
      'Premium pricing on all terms',
      '12-month minimum commitment',
      'High volume requirements',
    ],
    reliability: 0.995,
    maintenanceBonus: -0.03, // -3% maintenance costs
    negotiationStyle: 'premium',
    minContractMonths: 12,
    maxContractMonths: 36,
    maxAllocationGal: 5000000,
    minAllocationGal: 200000,
    hedgingAvailable: true,
    unlockedAtTier: 3,
  },
};

// ============================================================================
// MOOD SYSTEM
// ============================================================================

export const MOOD_LEVELS = {
  EAGER: { threshold: 80, color: '#22c55e', emoji: '😀', bidModifier: 0.92 },
  INTERESTED: { threshold: 60, color: '#84cc16', emoji: '🙂', bidModifier: 0.96 },
  NEUTRAL: { threshold: 40, color: '#eab308', emoji: '😐', bidModifier: 1.0 },
  HESITANT: { threshold: 20, color: '#f97316', emoji: '😤', bidModifier: 1.08 },
  INSULTED: { threshold: 0, color: '#ef4444', emoji: '😠', bidModifier: null }, // Walk-away
};

export function getMoodFromScore(score) {
  if (score >= MOOD_LEVELS.EAGER.threshold) return 'EAGER';
  if (score >= MOOD_LEVELS.INTERESTED.threshold) return 'INTERESTED';
  if (score >= MOOD_LEVELS.NEUTRAL.threshold) return 'NEUTRAL';
  if (score >= MOOD_LEVELS.HESITANT.threshold) return 'HESITANT';
  return 'INSULTED';
}

// ============================================================================
// STATE INITIALIZATION
// ============================================================================

export function createFuelState() {
  return {
    // Pricing
    spotPrice: BASE_SPOT_PRICE,
    priceHistory: [],

    // Supplier Contracts
    contracts: [],

    // Hedging
    hedges: [],

    // Active Negotiation
    activeNegotiation: null,

    // Statistics
    totalFuelPurchased: 0,
    totalFuelCost: 0,
    hedgeSavings: 0,
    contractSavings: 0,
    averagePurchasePrice: 0,
  };
}

// ============================================================================
// PRICE SIMULATION
// ============================================================================

/**
 * Calculate spot price based on macro system
 */
export function calculateSpotPrice(macroState) {
  const multiplier = macroState?.jetFuelPriceMultiplier || 1.0;
  // Add daily micro-volatility on top of monthly macro drift
  const dailyNoise = 0.97 + (Math.random() * 0.06); // +/- 3% daily
  let price = BASE_SPOT_PRICE * multiplier * dailyNoise;

  // Clamp to floor/ceiling
  return Math.max(PRICE_FLOOR, Math.min(PRICE_CEILING, price));
}

/**
 * Update fuel prices daily - called from GameLoopEngine
 */
export function tickFuelPriceDaily(fuelState, macroState, gameDate) {
  const newPrice = calculateSpotPrice(macroState);
  const dateStr = typeof gameDate === 'string' ? gameDate : gameDate.toISOString();

  // Maintain 30-day rolling history
  const newHistory = [
    ...fuelState.priceHistory.slice(-29),
    { date: dateStr, price: newPrice }
  ];

  return {
    ...fuelState,
    spotPrice: Math.round(newPrice * 100) / 100, // Round to cents
    priceHistory: newHistory,
  };
}

/**
 * Get price trend (change from yesterday)
 */
export function getPriceTrend(fuelState) {
  if (fuelState.priceHistory.length < 2) return { change: 0, trend: 'stable' };

  const yesterday = fuelState.priceHistory[fuelState.priceHistory.length - 2]?.price || fuelState.spotPrice;
  const change = fuelState.spotPrice - yesterday;

  return {
    change: Math.round(change * 100) / 100,
    trend: change > 0.02 ? 'up' : change < -0.02 ? 'down' : 'stable',
  };
}

// ============================================================================
// HEDGING MECHANICS
// ============================================================================

const HEDGE_DURATIONS = {
  SHORT: { months: 3, premiumMultiplier: 1.0, label: '3 months' },
  MEDIUM: { months: 6, premiumMultiplier: 0.9, label: '6 months' },
  LONG: { months: 12, premiumMultiplier: 0.8, label: '12 months' },
};

export function getHedgeDurations() {
  return HEDGE_DURATIONS;
}

/**
 * Calculate hedge premium cost
 */
export function calculateHedgePremium(gallons, lockPrice, durationMonths, macroState) {
  const volatility = macroState?.global?.OPVI || 0.35;
  const basePremiumRate = 0.05; // 5% of contract value
  const volatilityMultiplier = 1 + (volatility * 0.6);

  // Find duration multiplier
  let durationMultiplier = 1.0;
  for (const [key, config] of Object.entries(HEDGE_DURATIONS)) {
    if (config.months === durationMonths) {
      durationMultiplier = config.premiumMultiplier;
      break;
    }
  }

  const contractValue = gallons * lockPrice;
  return Math.round(contractValue * basePremiumRate * volatilityMultiplier * durationMultiplier);
}

/**
 * Calculate break-even price for a hedge
 */
export function calculateBreakEven(lockPrice, premium, gallons) {
  return Math.round((lockPrice + (premium / gallons)) * 100) / 100;
}

/**
 * Create a new hedge contract
 */
export function createHedgeContract(fuelState, params, cash, gameDate, currentMonth) {
  const { gallons, lockPrice, durationMonths, macroState } = params;

  const premium = calculateHedgePremium(gallons, lockPrice, durationMonths, macroState);

  if (cash < premium) {
    return { success: false, error: 'Insufficient funds for hedge premium' };
  }

  const breakEven = calculateBreakEven(lockPrice, premium, gallons);

  const hedge = {
    id: `HEDGE-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    lockPrice,
    gallons,
    gallonsRemaining: gallons,
    premium,
    startMonth: currentMonth + 1, // Starts next month
    durationMonths,
    monthsRemaining: durationMonths,
    breakEvenPrice: breakEven,
    status: 'pending',
    createdAt: typeof gameDate === 'string' ? gameDate : gameDate.toISOString(),
  };

  return {
    success: true,
    hedge,
    cost: premium,
    newFuelState: {
      ...fuelState,
      hedges: [...fuelState.hedges, hedge],
    },
  };
}

// ============================================================================
// SUPPLIER NEGOTIATION
// ============================================================================

/**
 * Calculate initial mood based on player stats
 */
function calculateInitialMood(supplier, reputationTier, existingRelation) {
  let baseMood = 50; // Neutral start

  // Reputation tier bonus
  baseMood += (reputationTier - 1) * 5; // +5 per tier above 1

  // Existing relationship
  baseMood += (existingRelation || 0) * 0.2;

  // Supplier personality
  switch (supplier.negotiationStyle) {
    case 'friendly': baseMood += 10; break;
    case 'desperate': baseMood += 15; break;
    case 'professional': baseMood += 0; break;
    case 'premium': baseMood -= 5; break;
  }

  return Math.max(0, Math.min(100, baseMood));
}

/**
 * Calculate supplier's walk-away threshold (hidden)
 */
function calculateWalkAwayThreshold(supplier, marketConditions) {
  const baseFloor = supplier.baseRate * 0.85; // Won't go below 85% of base
  const volatility = marketConditions?.OPVI || 0.35;

  // Higher volatility = suppliers less flexible
  const volatilityAdjust = volatility * 0.1;

  return baseFloor * (1 + volatilityAdjust);
}

/**
 * Start a new supplier negotiation
 */
export function startSupplierNegotiation(fuelState, supplierId, reputationTier, macroState) {
  const supplier = FUEL_SUPPLIERS[supplierId];
  if (!supplier) {
    return { success: false, error: 'Invalid supplier' };
  }

  // Check if player meets tier requirement
  if (reputationTier < supplier.unlockedAtTier) {
    return { success: false, error: `Requires Tier ${supplier.unlockedAtTier} reputation` };
  }

  // Check for existing contract
  const existingContract = fuelState.contracts.find(
    c => c.supplierId === supplierId && c.status === 'active'
  );

  // Get existing relation (0-100)
  const existingRelation = existingContract?.relation || 0;

  const initialMood = calculateInitialMood(supplier, reputationTier, existingRelation);
  const walkAwayThreshold = calculateWalkAwayThreshold(supplier, macroState?.global);

  return {
    success: true,
    newFuelState: {
      ...fuelState,
      activeNegotiation: {
        supplierId,
        phase: 'initial',
        startTime: Date.now(),
        mood: getMoodFromScore(initialMood),
        moodScore: initialMood,
        offers: [],
        currentOffer: null,
        supplierLastOffer: {
          rate: supplier.baseRate,
          allocationGal: Math.round(supplier.maxAllocationGal / 2),
          durationMonths: supplier.minContractMonths,
        },
        walkAwayThreshold,
        counterLimit: 3,
        countersUsed: 0,
        isRenewal: !!existingContract,
      },
    },
  };
}

/**
 * Calculate aggressiveness of a player offer
 */
function calculateAggressiveness(playerOffer, supplierOffer, supplier) {
  // How far below supplier's offer is the player?
  const rateRatio = playerOffer.rate / supplierOffer.rate;

  // Below 90% is aggressive, below 80% is very aggressive
  if (rateRatio < 0.80) return 30; // Very aggressive
  if (rateRatio < 0.85) return 20; // Aggressive
  if (rateRatio < 0.90) return 10; // Somewhat aggressive
  if (rateRatio < 0.95) return 5;  // Mild push
  return 0; // Reasonable
}

/**
 * Generate supplier counter-offer
 */
function generateCounterOffer(playerOffer, negotiation, supplier) {
  const moodLevel = MOOD_LEVELS[negotiation.mood];
  const modifier = moodLevel.bidModifier || 1.0;

  // Supplier moves toward player but modified by mood
  const playerRate = playerOffer.rate;
  const lastRate = negotiation.supplierLastOffer.rate;

  // Move 20-40% toward player offer, depending on mood
  const movePercent = 0.2 + (0.2 * (1 - modifier)); // Eager = move more
  const newRate = lastRate - (lastRate - playerRate) * movePercent;

  // Don't go below walk-away threshold
  const finalRate = Math.max(newRate, negotiation.walkAwayThreshold);

  return {
    rate: Math.round(finalRate * 100) / 100,
    allocationGal: playerOffer.allocationGal,
    durationMonths: playerOffer.durationMonths,
  };
}

/**
 * Submit a player offer in negotiation
 */
export function submitNegotiationOffer(fuelState, offer) {
  const neg = fuelState.activeNegotiation;
  if (!neg) return { success: false, error: 'No active negotiation' };

  const supplier = FUEL_SUPPLIERS[neg.supplierId];

  // Calculate mood impact
  const aggressiveness = calculateAggressiveness(offer, neg.supplierLastOffer, supplier);
  let newMoodScore = neg.moodScore - aggressiveness;

  // Check for walk-away
  if (newMoodScore <= MOOD_LEVELS.INSULTED.threshold) {
    return {
      success: true,
      result: 'WALKED_AWAY',
      newFuelState: {
        ...fuelState,
        activeNegotiation: {
          ...neg,
          phase: 'result',
          moodScore: newMoodScore,
          mood: 'INSULTED',
          result: 'WALKED_AWAY',
        },
      },
    };
  }

  // Check for acceptance (offer meets or exceeds supplier's last offer)
  if (offer.rate >= neg.supplierLastOffer.rate) {
    return {
      success: true,
      result: 'ACCEPTED',
      newFuelState: {
        ...fuelState,
        activeNegotiation: {
          ...neg,
          phase: 'result',
          currentOffer: offer,
          result: 'DEAL',
          finalRate: offer.rate,
        },
      },
    };
  }

  // Generate counter-offer
  const counterOffer = generateCounterOffer(offer, neg, supplier);
  const countersUsed = neg.countersUsed + 1;

  // Check if this is the final round
  const isFinalRound = countersUsed >= neg.counterLimit;

  return {
    success: true,
    result: 'COUNTER',
    counterOffer,
    newFuelState: {
      ...fuelState,
      activeNegotiation: {
        ...neg,
        phase: isFinalRound ? 'final' : 'counter',
        moodScore: newMoodScore,
        mood: getMoodFromScore(newMoodScore),
        offers: [...neg.offers, { type: 'player', ...offer, timestamp: Date.now() }],
        supplierLastOffer: counterOffer,
        currentOffer: offer,
        countersUsed,
      },
    },
  };
}

/**
 * Accept supplier's current offer
 */
export function acceptSupplierOffer(fuelState) {
  const neg = fuelState.activeNegotiation;
  if (!neg) return { success: false, error: 'No active negotiation' };

  return {
    success: true,
    result: 'DEAL',
    finalRate: neg.supplierLastOffer.rate,
    newFuelState: {
      ...fuelState,
      activeNegotiation: {
        ...neg,
        phase: 'result',
        result: 'DEAL',
        finalRate: neg.supplierLastOffer.rate,
        finalAllocation: neg.supplierLastOffer.allocationGal,
        finalDuration: neg.supplierLastOffer.durationMonths,
      },
    },
  };
}

/**
 * Walk away from negotiation
 */
export function walkAwayFromNegotiation(fuelState) {
  const neg = fuelState.activeNegotiation;
  if (!neg) return { success: false, error: 'No active negotiation' };

  return {
    success: true,
    result: 'WALK',
    newFuelState: {
      ...fuelState,
      activeNegotiation: {
        ...neg,
        phase: 'result',
        result: 'WALK',
      },
    },
  };
}

/**
 * Complete negotiation and create contract
 */
export function completeNegotiation(fuelState, gameDate) {
  const neg = fuelState.activeNegotiation;
  if (!neg || neg.phase !== 'result') {
    return { success: false, error: 'Negotiation not in result phase' };
  }

  if (neg.result !== 'DEAL') {
    // No deal - just clear negotiation
    return {
      success: true,
      contract: null,
      newFuelState: {
        ...fuelState,
        activeNegotiation: null,
      },
    };
  }

  const supplier = FUEL_SUPPLIERS[neg.supplierId];
  const dateStr = typeof gameDate === 'string' ? gameDate : gameDate.toISOString();
  const startDate = new Date(dateStr);
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + neg.finalDuration);

  const contract = {
    id: `CONTRACT-${neg.supplierId}-${Date.now()}`,
    supplierId: neg.supplierId,
    supplierName: supplier.name,
    negotiatedRate: neg.finalRate,
    monthlyAllocationGal: neg.finalAllocation,
    minimumPurchaseGal: Math.round(neg.finalAllocation * 0.7), // 70% minimum
    coverageAirports: supplier.coverage,
    reliability: supplier.reliability,
    maintenanceBonus: supplier.maintenanceBonus,
    startDate: dateStr,
    endDate: endDate.toISOString(),
    usedThisMonth: 0,
    relation: 50, // Start neutral
    status: 'active',
  };

  return {
    success: true,
    contract,
    newFuelState: {
      ...fuelState,
      contracts: [...fuelState.contracts, contract],
      activeNegotiation: null,
    },
  };
}

/**
 * Cancel/timeout negotiation
 */
export function cancelNegotiation(fuelState) {
  return {
    ...fuelState,
    activeNegotiation: null,
  };
}

// ============================================================================
// FUEL COST CALCULATION (for FlightEarningsEngine integration)
// ============================================================================

/**
 * Get the effective fuel price for a flight
 * Priority: Contract > Hedge > Spot
 */
export function getEffectiveFuelPrice(fuelState, airportCode, gallonsNeeded) {
  // 1. Check for active contract covering this airport
  const contract = fuelState.contracts.find(c => {
    if (c.status !== 'active') return false;
    if (c.coverageAirports !== null && !c.coverageAirports.includes(airportCode)) return false;
    if (c.usedThisMonth >= c.monthlyAllocationGal) return false;
    return true;
  });

  if (contract) {
    const availableGal = contract.monthlyAllocationGal - contract.usedThisMonth;
    if (availableGal >= gallonsNeeded) {
      return {
        source: 'CONTRACT',
        price: contract.negotiatedRate,
        supplier: contract.supplierName,
        maintenanceBonus: contract.maintenanceBonus,
        contractId: contract.id,
      };
    }
  }

  // 2. Check for active hedge with available gallons
  const activeHedge = fuelState.hedges.find(h =>
    h.status === 'active' && h.gallonsRemaining >= gallonsNeeded
  );

  if (activeHedge) {
    return {
      source: 'HEDGE',
      price: activeHedge.lockPrice,
      hedgeId: activeHedge.id,
    };
  }

  // 3. Fall back to spot market
  return {
    source: 'SPOT',
    price: fuelState.spotPrice,
  };
}

/**
 * Record fuel usage (called after flight completes)
 */
export function recordFuelUsage(fuelState, gallons, fuelInfo) {
  let newState = { ...fuelState };

  // Update contract usage if applicable
  if (fuelInfo.source === 'CONTRACT' && fuelInfo.contractId) {
    newState.contracts = newState.contracts.map(c => {
      if (c.id === fuelInfo.contractId) {
        return { ...c, usedThisMonth: c.usedThisMonth + gallons };
      }
      return c;
    });

    // Track contract savings
    const savings = (newState.spotPrice - fuelInfo.price) * gallons;
    newState.contractSavings = (newState.contractSavings || 0) + Math.max(0, savings);
  }

  // Update hedge usage if applicable
  if (fuelInfo.source === 'HEDGE' && fuelInfo.hedgeId) {
    newState.hedges = newState.hedges.map(h => {
      if (h.id === fuelInfo.hedgeId) {
        return { ...h, gallonsRemaining: h.gallonsRemaining - gallons };
      }
      return h;
    });

    // Track hedge savings
    const savings = (newState.spotPrice - fuelInfo.price) * gallons;
    newState.hedgeSavings = (newState.hedgeSavings || 0) + Math.max(0, savings);
  }

  // Update totals
  newState.totalFuelPurchased = (newState.totalFuelPurchased || 0) + gallons;
  newState.totalFuelCost = (newState.totalFuelCost || 0) + (gallons * fuelInfo.price);
  newState.averagePurchasePrice = newState.totalFuelCost / newState.totalFuelPurchased;

  return newState;
}

// ============================================================================
// MONTHLY PROCESSING
// ============================================================================

/**
 * Process monthly fuel system updates
 * - Activate pending hedges
 * - Expire old hedges
 * - Check contract minimums
 * - Reset monthly usage
 */
export function processMonthlyFuel(fuelState, currentMonth, gameDate) {
  let newState = { ...fuelState };
  const penalties = [];

  // Process hedges
  newState.hedges = newState.hedges.map(h => {
    // Activate pending hedges
    if (h.status === 'pending' && currentMonth >= h.startMonth) {
      return { ...h, status: 'active' };
    }

    // Decrement and expire active hedges
    if (h.status === 'active') {
      const remaining = h.monthsRemaining - 1;
      if (remaining <= 0 || h.gallonsRemaining <= 0) {
        return { ...h, status: 'expired', monthsRemaining: 0 };
      }
      return { ...h, monthsRemaining: remaining };
    }

    return h;
  });

  // Process contracts
  newState.contracts = newState.contracts.map(c => {
    if (c.status !== 'active') return c;

    // Check if expired
    const endDate = new Date(c.endDate);
    const currentDate = new Date(gameDate);
    if (currentDate >= endDate) {
      return { ...c, status: 'expired' };
    }

    // Check minimum purchase requirement
    if (c.usedThisMonth < c.minimumPurchaseGal) {
      const shortfall = c.minimumPurchaseGal - c.usedThisMonth;
      const penalty = shortfall * 0.50; // $0.50 per gallon shortfall
      penalties.push({
        contractId: c.id,
        supplier: c.supplierName,
        shortfall,
        penalty,
      });

      // Damage relationship
      return {
        ...c,
        usedThisMonth: 0,
        relation: Math.max(0, c.relation - 10),
      };
    }

    // Good standing - improve relationship slightly
    return {
      ...c,
      usedThisMonth: 0,
      relation: Math.min(100, c.relation + 2),
    };
  });

  // Clean up expired hedges older than 3 months
  const threeMonthsAgo = currentMonth - 3;
  newState.hedges = newState.hedges.filter(h =>
    h.status !== 'expired' || h.startMonth > threeMonthsAgo
  );

  return {
    newFuelState: newState,
    penalties,
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format fuel price for display
 */
export function formatFuelPrice(price) {
  return `$${price.toFixed(2)}`;
}

/**
 * Format large gallon numbers
 */
export function formatGallons(gallons) {
  if (gallons >= 1000000) {
    return `${(gallons / 1000000).toFixed(1)}M`;
  }
  if (gallons >= 1000) {
    return `${(gallons / 1000).toFixed(0)}K`;
  }
  return gallons.toLocaleString();
}

/**
 * Check if supplier is available to player
 */
export function isSupplierAvailable(supplierId, reputationTier) {
  const supplier = FUEL_SUPPLIERS[supplierId];
  return supplier && reputationTier >= supplier.unlockedAtTier;
}

/**
 * Get list of available suppliers for player
 */
export function getAvailableSuppliers(reputationTier) {
  return Object.values(FUEL_SUPPLIERS).filter(
    s => reputationTier >= s.unlockedAtTier
  );
}

// ============================================================================
// SIMPLE MODE - Market Trends & Dynamic Pricing
// ============================================================================

/**
 * Roll a new market trend when the current one expires
 * @param {number} currentPrice - Current spot price
 * @returns {Object} New trend object
 */
export function rollNewTrend(currentPrice) {
  const roll = Math.random();

  if (roll < 0.35) {
    // Inflationary - prices rising (35% chance)
    return {
      direction: 'inflationary',
      strength: 0.01 + Math.random() * 0.02,  // 1-3% daily push
      daysRemaining: 30 + Math.floor(Math.random() * 60),  // 30-90 days
      startPrice: currentPrice,
    };
  } else if (roll < 0.70) {
    // Deflationary - prices falling (35% chance)
    return {
      direction: 'deflationary',
      strength: 0.01 + Math.random() * 0.02,
      daysRemaining: 30 + Math.floor(Math.random() * 60),
      startPrice: currentPrice,
    };
  } else {
    // Stable - prices flat (30% chance)
    return {
      direction: 'stable',
      strength: 0,
      daysRemaining: 20 + Math.floor(Math.random() * 40),  // 20-60 days
      startPrice: currentPrice,
    };
  }
}

/**
 * Update fuel price based on trend and daily noise
 * @param {number} currentPrice - Current spot price
 * @param {Object} trend - Current market trend
 * @param {Array} worldEvents - Active world events (optional)
 * @returns {number} New spot price
 */
export function updateFuelPriceWithTrend(currentPrice, trend, worldEvents = []) {
  // Apply trend direction
  let trendModifier = 1.0;
  if (trend.direction === 'inflationary') {
    trendModifier = 1 + trend.strength;  // push price up
  } else if (trend.direction === 'deflationary') {
    trendModifier = 1 - trend.strength;  // push price down
  }

  // Base daily variance: ±3% noise
  const dailyNoise = 0.97 + (Math.random() * 0.06);

  // Event modifiers (stack on top of trend)
  let eventModifier = 1.0;
  if (worldEvents.includes('oil_crisis')) eventModifier = 1.15;
  if (worldEvents.includes('refinery_issue')) eventModifier = 1.08;
  if (worldEvents.includes('oversupply')) eventModifier = 0.92;
  if (worldEvents.includes('stable_markets')) eventModifier = 0.98;

  // Calculate new price
  let newPrice = currentPrice * dailyNoise * trendModifier * eventModifier;

  // Clamp to range ($2.00 - $6.00)
  newPrice = Math.max(2.00, Math.min(6.00, newPrice));

  return Math.round(newPrice * 100) / 100; // Round to cents
}

/**
 * Process daily fuel update for Simple Mode
 * Updates price, trend, and history
 * @param {Object} worldState - Current world state
 * @returns {Object} Updates to apply to worldState
 */
export function processDailyFuelSimple(worldState) {
  const currentPrice = worldState.fuelSpotPrice || 3.00;
  let trend = worldState.fuelMarketTrend || {
    direction: 'stable',
    strength: 0,
    daysRemaining: 30,
    startPrice: 3.00,
  };
  const history = worldState.fuelPriceHistory || [];
  const activeEvents = worldState.activeEvents || [];
  const gameDate = worldState.gameTime || new Date().toISOString();

  // Decrement trend days
  trend = {
    ...trend,
    daysRemaining: trend.daysRemaining - 1,
  };

  // If trend expired, roll new one
  if (trend.daysRemaining <= 0) {
    trend = rollNewTrend(currentPrice);
  }

  // Update price
  const newPrice = updateFuelPriceWithTrend(currentPrice, trend, activeEvents);

  // Add to history (keep last 30 days)
  const newHistory = [
    ...history.slice(-29),
    { date: gameDate, price: newPrice },
  ];

  return {
    fuelSpotPrice: newPrice,
    fuelMarketTrend: trend,
    fuelPriceHistory: newHistory,
  };
}

/**
 * Get trend display info for UI
 * @param {Object} trend - Current market trend
 * @returns {Object} { icon, label, color, description }
 */
export function getTrendDisplayInfo(trend) {
  if (!trend) {
    return { icon: '➡️', label: 'STABLE', color: '#737373', description: 'Prices stable' };
  }

  switch (trend.direction) {
    case 'inflationary':
      return {
        icon: '📈',
        label: 'INFLATIONARY',
        color: '#ef4444',
        description: 'Prices trending upward',
      };
    case 'deflationary':
      return {
        icon: '📉',
        label: 'DEFLATIONARY',
        color: '#22c55e',
        description: 'Prices trending downward',
      };
    case 'stable':
    default:
      return {
        icon: '➡️',
        label: 'STABLE',
        color: '#737373',
        description: 'Prices stable',
      };
  }
}

// ============================================================================
// SIMPLE MODE - Fuel Contracts
// ============================================================================

/**
 * Contract options available to players
 */
export const CONTRACT_OPTIONS = [
  { days: 30, discount: 0.02, minimumGallons: 100000, label: '30-Day' },
  { days: 60, discount: 0.05, minimumGallons: 250000, label: '60-Day' },
  { days: 90, discount: 0.08, minimumGallons: 500000, label: '90-Day' },
];

/**
 * Check if player can sign a new contract
 */
export function canSignContract(worldState) {
  const contract = worldState?.fuelContract;
  // Can sign if no contract or current contract is not active
  return !contract || contract.status !== 'active';
}

/**
 * Sign a new fuel contract
 * @param {Object} worldState - Current world state
 * @param {number} durationDays - Contract duration (30, 60, or 90)
 * @returns {Object} { success, error?, newContract? }
 */
export function signFuelContract(worldState, durationDays) {
  // Check if player already has active contract
  if (!canSignContract(worldState)) {
    return { success: false, error: 'Already have an active contract' };
  }

  // Find the contract option
  const option = CONTRACT_OPTIONS.find(o => o.days === durationDays);
  if (!option) {
    return { success: false, error: 'Invalid contract duration' };
  }

  const currentPrice = worldState.fuelSpotPrice || 3.00;
  const lockedPrice = Math.round(currentPrice * (1 - option.discount) * 100) / 100;
  const dayNumber = worldState.dayNumber || 1;

  const newContract = {
    id: `contract-${Date.now()}`,
    lockedPrice,
    discount: option.discount,
    startDay: dayNumber,
    endDay: dayNumber + option.days,
    durationDays: option.days,
    minimumGallons: option.minimumGallons,
    purchasedGallons: 0,
    status: 'active',
  };

  return {
    success: true,
    newContract,
  };
}

/**
 * Get the effective purchase price (considers contract, hedge, and chaos)
 * @param {Object} worldState - Current world state
 * @returns {number} Effective price per gallon
 */
export function getEffectivePurchasePrice(worldState) {
  const spotPrice = worldState?.fuelSpotPrice || 3.00;
  const contract = worldState?.fuelContract;
  const hedge = worldState?.fuelHedge;
  const dayNumber = worldState?.dayNumber || 1;
  const chaos = worldState?.fuelMarketChaos;

  let effectivePrice = spotPrice;

  // MARKET MAKER CHAOS: Override spot price with wild random swings
  if (chaos && chaos.active && dayNumber <= chaos.endDay) {
    const range = chaos.priceMax - chaos.priceMin;
    effectivePrice = chaos.priceMin + (Math.random() * range);
    effectivePrice = Math.round(effectivePrice * 100) / 100; // Round to cents
    debug(`[FuelSystem] CHAOS PRICE: $${effectivePrice.toFixed(2)}/gal (range: $${chaos.priceMin}-$${chaos.priceMax})`);
    // Chaos overrides contracts and hedges - it's CHAOS
    return effectivePrice;
  }

  // If contract is active and not expired, use contract price
  if (contract && contract.status === 'active' && dayNumber <= contract.endDay) {
    effectivePrice = contract.lockedPrice;
  }

  // If hedge is active and spot is above cap, use cap price
  if (hedge && hedge.status === 'active' && dayNumber <= hedge.endDay) {
    effectivePrice = Math.min(effectivePrice, hedge.capPrice);
  }

  // MYTHIC BUFF: Half price fuel (check real time expiration)
  const halfPriceBuff = worldState?.buffs?.halfPriceFuel;
  if (halfPriceBuff?.active && halfPriceBuff?.expiresAt && Date.now() < halfPriceBuff.expiresAt) {
    effectivePrice = effectivePrice * 0.5;
    debug(`[FuelSystem] MYTHIC BUFF: Half-price fuel active! $${effectivePrice.toFixed(2)}/gal`);
  }

  return effectivePrice;
}

/**
 * Record a fuel purchase against active contract
 * @param {Object} contract - Current contract
 * @param {number} gallons - Gallons purchased
 * @returns {Object} Updated contract
 */
export function recordContractPurchase(contract, gallons) {
  if (!contract || contract.status !== 'active') return contract;

  return {
    ...contract,
    purchasedGallons: contract.purchasedGallons + gallons,
  };
}

/**
 * Check contract status at end of day / expiration
 * @param {Object} contract - Current contract
 * @param {number} dayNumber - Current day number
 * @returns {Object} Updated contract with new status
 */
export function checkContractExpiration(contract, dayNumber) {
  if (!contract || contract.status !== 'active') return contract;

  // Check if expired
  if (dayNumber > contract.endDay) {
    const metMinimum = contract.purchasedGallons >= contract.minimumGallons;
    return {
      ...contract,
      status: metMinimum ? 'fulfilled' : 'expired',
    };
  }

  return contract;
}

// ============================================================================
// SIMPLE MODE - Fuel Consumption
// ============================================================================

/**
 * Calculate fuel needed for a flight
 * @param {number} flightTimeHours - Flight duration in hours
 * @param {number} fuelBurnRate - Aircraft fuel burn rate (gal/hour)
 * @returns {number} Gallons of fuel needed
 */
export function calculateFuelNeeded(flightTimeHours, fuelBurnRate) {
  // Add 10% reserve for safety
  const baseFuel = flightTimeHours * fuelBurnRate;
  const reserveFuel = baseFuel * 0.10;
  return Math.ceil(baseFuel + reserveFuel);
}

/**
 * Check if there's enough fuel for a flight
 * @param {number} fuelReserves - Current fuel in tanks
 * @param {number} fuelNeeded - Fuel required for the flight
 * @returns {{ canFly: boolean, shortage: number }}
 */
export function checkFuelAvailability(fuelReserves, fuelNeeded) {
  const shortage = fuelNeeded - fuelReserves;
  return {
    canFly: fuelReserves >= fuelNeeded,
    shortage: shortage > 0 ? shortage : 0,
    fuelNeeded,
  };
}

/**
 * Get fuel warning level based on percentage
 * @param {number} fuelReserves - Current fuel
 * @param {number} maxFuel - Max fuel capacity
 * @returns {{ level: string, color: string, message: string }}
 */
export function getFuelWarningLevel(fuelReserves, maxFuel) {
  const percent = (fuelReserves / maxFuel) * 100;

  if (percent <= 0) {
    return {
      level: 'empty',
      color: '#ef4444',
      message: 'FUEL EMPTY - Cannot dispatch flights',
      canFly: false,
    };
  }
  if (percent <= 10) {
    return {
      level: 'critical',
      color: '#ef4444',
      message: 'CRITICAL FUEL - Refuel immediately',
      canFly: true,
    };
  }
  if (percent <= 20) {
    return {
      level: 'low',
      color: '#f59e0b',
      message: 'LOW FUEL WARNING',
      canFly: true,
    };
  }
  return {
    level: 'ok',
    color: '#22c55e',
    message: null,
    canFly: true,
  };
}

// ============================================================================
// SIMPLE MODE - Fuel Hedging
// ============================================================================

/**
 * Hedge options available to players
 * Cap = spot price + 10% at time of purchase
 * Player pays whichever is LOWER: spot or cap
 */
export const HEDGE_OPTIONS = [
  { days: 30, premium: 50000, label: '30-Day' },
  { days: 60, premium: 90000, label: '60-Day' },
  { days: 90, premium: 120000, label: '90-Day' },
];

/**
 * Check if player can purchase a new hedge
 * Can have both contract AND hedge active simultaneously
 */
export function canPurchaseHedge(worldState) {
  const hedge = worldState?.fuelHedge;
  const dayNumber = worldState?.dayNumber || 1;
  // Can purchase if no hedge or current hedge is expired
  return !hedge || hedge.status !== 'active' || dayNumber > hedge.endDay;
}

/**
 * Purchase a fuel hedge
 * @param {Object} worldState - Current world state
 * @param {number} durationDays - Hedge duration (30, 60, or 90)
 * @returns {Object} { success, error?, newHedge?, cost? }
 */
export function purchaseFuelHedge(worldState, durationDays) {
  const cash = worldState?.airline?.cash || 0;
  const dayNumber = worldState?.dayNumber || 1;
  const currentPrice = worldState?.fuelSpotPrice || 3.00;

  // Check if player already has active hedge
  if (!canPurchaseHedge(worldState)) {
    return { success: false, error: 'Already have an active hedge' };
  }

  // Find the hedge option
  const option = HEDGE_OPTIONS.find(o => o.days === durationDays);
  if (!option) {
    return { success: false, error: 'Invalid hedge duration' };
  }

  // Check if player can afford the premium
  if (cash < option.premium) {
    return { success: false, error: `Insufficient funds. Need $${(option.premium / 1000).toFixed(0)}K` };
  }

  // Cap = spot price + 10%
  const capPrice = Math.round(currentPrice * 1.10 * 100) / 100;

  const newHedge = {
    id: `hedge-${Date.now()}`,
    capPrice,
    premiumPaid: option.premium,
    startDay: dayNumber,
    endDay: dayNumber + option.days,
    durationDays: option.days,
    status: 'active',
    spotAtPurchase: currentPrice,
  };

  return {
    success: true,
    newHedge,
    cost: option.premium,
  };
}

/**
 * Check hedge status at end of day / expiration
 * @param {Object} hedge - Current hedge
 * @param {number} dayNumber - Current day number
 * @returns {Object} Updated hedge with new status
 */
export function checkHedgeExpiration(hedge, dayNumber) {
  if (!hedge || hedge.status !== 'active') return hedge;

  // Check if expired
  if (dayNumber > hedge.endDay) {
    return {
      ...hedge,
      status: 'expired',
    };
  }

  return hedge;
}
